import { ArrowRight, Landmark, Loader2 } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useCrmProposal } from '../hooks/useCrmProposal';
import { usePendingAccountDraftStore } from '../stores/pendingAccountDraftStore';
import { useSoteriaSidePanelStore } from '../stores/sidePanelStore';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import { ChangeRows } from './CrmProposalCard';
import type {
  AccountDraftCard as AccountDraftCardData,
  CrmProposalCard,
} from '../hooks/useSoteriaChat';
import { cn } from '@/lib/utils';

/**
 * AccountDraftCard
 * ----------------
 * The Client Assistant's "open account" hand-off. Shows the proposed custodian
 * + account type(s) for a client and a "Review & open" button that stashes the
 * draft and jumps into the account-opening wizard, pre-filled at step one. The
 * advisor then completes KYC/compliance and signs — the agent opens nothing.
 *
 * The wizard fills the applicant from the client's RECORD, so details the
 * assistant gathered that the record lacks are saved there first. The assistant
 * does not save them: they arrive as a `profile_proposal` listed on this card,
 * and the button confirms them (once, server-side) before opening the wizard. If
 * that is refused or has expired the wizard is not opened, so the advisor is
 * never left believing details were saved that were not.
 */
export function AccountDraftCard({
  draft,
  sessionId,
}: {
  draft: AccountDraftCardData;
  sessionId?: string | null;
}) {
  // A proposal object is always passed to the hook (it is called
  // unconditionally); an inert stand-in is one that is already `confirmed`.
  const proposal = draft.profile_proposal ?? NO_PROPOSAL;
  const profile = useCrmProposal(proposal, sessionId);
  const hasProposal = !!draft.profile_proposal;
  const pendingSave = hasProposal && profile.phase === 'pending';

  const navigate = useNavigate();
  const setPending = usePendingAccountDraftStore((s) => s.setPending);
  const closePanel = useSoteriaSidePanelStore((s) => s.close);

  const openWizard = () => {
    setPending({
      clientId: draft.client_id,
      clientName: draft.client_name,
      custodian: draft.custodian === 'NBIN' ? 'NBIN' : 'IBKR',
      counts: draft.counts,
      details: draft.details,
      holders: draft.joint_holders,
      contacts: draft.contacts,
    });
    // The wizard is a full-viewport takeover; close the drawer behind it.
    closePanel();
    void navigate({
      to: '/clients/$clientId/open-account',
      params: { clientId: draft.client_id },
    });
  };

  const handleReview = async () => {
    if (pendingSave && !(await profile.confirm())) return;
    openWizard();
  };

  // "Open without saving" drops the proposal first, so nothing can be confirmed
  // from this card afterwards.
  const handleSkip = async () => {
    if (pendingSave && !(await profile.cancel())) return;
    openWizard();
  };

  return (
    <SoteriaCard
      variant="default"
      icon={<Landmark className="h-4 w-4" />}
      eyebrow="Open account: draft ready"
      title={`${draft.client_name} · ${draft.custodian}`}
      cta={
        <button
          type="button"
          onClick={() => void handleReview()}
          disabled={profile.busy || (pendingSave && !profile.canAct)}
          className={cn(
            soteriaCardCtaClass('default'),
            'gap-1.5 disabled:opacity-60',
          )}
        >
          {profile.busy && <Loader2 className="h-3 w-3 animate-spin" />}
          {pendingSave ? 'Save details & open' : 'Review & open'}
          <ArrowRight className="h-3 w-3" />
        </button>
      }
    >
      <div className="mt-3 flex flex-wrap gap-1.5">
        {draft.account_types.map((t) => (
          <span
            key={t.code}
            className="inline-flex items-center rounded-pill border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
          >
            {t.label}
            {(draft.counts[t.code] ?? 1) > 1 && ` ×${draft.counts[t.code]}`}
          </span>
        ))}
      </div>

      {pendingSave && (
        <>
          <p className="mt-3 text-xs font-medium text-foreground">
            Not saved yet: these details will be added to {draft.client_name}
            &apos;s record when you confirm.
          </p>
          <ChangeRows changes={proposal.changes} />
          <button
            type="button"
            onClick={() => void handleSkip()}
            disabled={profile.busy || !profile.canAct}
            className="mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-60"
          >
            Open without saving these
          </button>
        </>
      )}
      {hasProposal && profile.phase === 'cancelled' && (
        <p className="mt-3 text-xs text-muted-foreground">
          The extra details were not saved; the wizard shows what is on file.
        </p>
      )}
      {hasProposal &&
        (profile.phase === 'expired' || profile.phase === 'refused') && (
          <p className="mt-3 text-xs font-medium text-foreground">
            {profile.message} The wizard shows what is on file.
          </p>
        )}

      <p className="mt-3 text-xs text-muted-foreground">
        You'll review the client's details, complete KYC &amp; compliance, and
        sign in the wizard. Nothing is submitted until you do.
      </p>
    </SoteriaCard>
  );
}

// The hook is called on every render, so a draft with nothing to save passes
// this already-confirmed stand-in and never uses it.
const NO_PROPOSAL: CrmProposalCard = {
  id: '',
  action: 'update_client_profile',
  state: 'confirmed',
  expires_at: '',
  title: '',
  client_id: '',
  changes: [],
};
